import { Icon } from "@iconify/react/dist/iconify.js";
import { Box, Divider, Paper, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ResizableBox } from "react-resizable";
import "react-resizable/css/styles.css";
import { useAuth } from "../../authentication/AuthProvider";
import RefreshButton from "../../components/button/RefreshButton";
import { MainContainer } from "../../components/container/MainContainer";
import { PageTitleContainer } from "../../components/container/PageTitleContainer";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableHeaderToolsContainer } from "../../components/container/TableHeaderToolsContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import ContextMenu from "../../components/context-menu/ContextMenu";
import DialogDelete from "../../components/dialog/DialogDelete";
import ExportAsXLSXButton from "../../components/export/ExportAsXLSXButton";
import { centerAlignColDef, defaultColDef, floatFormater, leftAlignColDef, rightAlignColDef } from "../../components/grid/columnDefinitions/columnDefinitions";
import SearchBox from "../../components/input/SearchBox";
import ThirdTabs from "../../components/tabs/ThirdTabs";
import { InvitationCollaborateProduct } from "../../db_access/invitation_collaborate_product";
import { InvitationCollaborateProject } from "../../db_access/invitation_collaborate_project";
import { Notification } from "../../db_access/notification";
import { Process } from "../../db_access/process";
import { Product } from "../../db_access/product";
import { Project } from "../../db_access/project";
import Footprint from "../../environment-page-components/Footprint";
import SankeyDiagram from "../../environment-page-components/SankeyDiagram";
import SystemBoundary from "../../environment-page-components/SystemBoundary";
import "../../style/sidemenu-pages-style/UserLibrary.css";
import InvitationProduct from "../Notification/InvitationProduct";
import InvitationProject from "../Notification/InvitationProject";
import DialogSend from "../Products/Collaborate/DialogSend";

const getCollaborates = (request, _type) =>
  request()
    .then((res) => res.json())
    .then((data) => data.map((d) => ({ ...d, _type })));

const getCollaboratesProduct = (request) => getCollaborates(request, "Product");
const getCollaboratesProject = (request) => getCollaborates(request, "Project");
export const getMineCollaboratesProduct = () => getCollaboratesProduct(Product.get_mine_collaborates);
export const getMineCollaboratesProject = () => getCollaboratesProject(Project.get_mine_collaborates);

export const getExternalCollaboratesProduct = () => getCollaboratesProduct(Product.get_external_collaborates);
export const getExternalCollaboratesProject = () => getCollaboratesProject(Project.get_external_collaborates);

export const getInternalCollaboratesProduct = () => getCollaboratesProduct(Product.get_internal_collaborates);
export const getInternalCollaboratesProject = () => getCollaboratesProject(Project.get_internal_collaborates);

export const duplicateToMeProduct = (products) => Product.duplicate(products.filter((s) => s._type === "Product").map((s) => s.id));
export const duplicateToMeProject = (projects) => Project.duplicate(projects.filter((s) => s._type === "Project").map((s) => s.id));

export const uncollaborateProduct = (products) => Product.uncollaborate(products.filter((s) => s._type === "Product").map((s) => s.id));
export const uncollaborateProject = (projects) => Project.uncollaborate(projects.filter((s) => s._type === "Project").map((s) => s.id));

export const deleteProduct = (products) => Product.delete(products.filter((s) => s._type === "Product").map((s) => s.id));
export const deleteProject = (projects) => Project.delete(projects.filter((s) => s._type === "Project").map((s) => s.id));

const CollaborateDashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const pageType = "supplier";
  const [invitationProducts, setInvitationProducts] = useState([]);
  const [invitationProjects, setInvitationProjects] = useState([]);
  const [externalCollaborates, setExternalCollaborates] = useState([]);
  const [mineCollaborates, setMineCollaborates] = useState([]);
  const [internalCollaborates, setInternalCollaborates] = useState([]);

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [processData, setProcessData] = useState([]);
  const [refresh, setRefresh] = useState(true);
  const [height, setHeight] = useState(600);
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const gridRef = useRef(null);
  const dialogDeleteRef = useRef(null);
  const dialogRef = useRef();
  const handleOpenDialog = () => dialogRef.current?.open();
  const mine = { label: "Mine", value: "Mine" };
  const internal = { label: "Internal", value: "internal" };
  const external = { label: "External", value: "external" };
  let tabs = [mine, internal];
  const isCompanAdmin = user["is_company_admin"] === 1;
  if (isCompanAdmin) {
    tabs.push(external);
  }
  const [selectedTab, setSelectedTab] = useState("Mine");
  const [searchText, setSearchText] = useState("");
  const getMineCollaborates = () => {
    Promise.all([getMineCollaboratesProduct(), getMineCollaboratesProject()])
      .then(([products, projects]) => setMineCollaborates([...products, ...projects]))
      .catch((error) => console.error("Error fetching data:", error));
  };
  const getInternalCollaborates = () => {
    Promise.all([getInternalCollaboratesProduct(), getInternalCollaboratesProject()])
      .then(([products, projects]) => setInternalCollaborates([...products, ...projects]))
      .catch((error) => console.error("Error fetching data:", error));
  };
  const getExternalCollaborates = () => {
    Promise.all([getExternalCollaboratesProduct(), getExternalCollaboratesProject()])
      .then(([products, projects]) => setExternalCollaborates([...products, ...projects]))
      .catch((error) => console.error("Error fetching data:", error));
  };
  const duplicateToMe = (collaborates) => {
    Promise.all([duplicateToMeProduct(collaborates), duplicateToMeProject(collaborates)])
      .catch((error) => console.error("Error fetching data:", error))
      .finally(() => setRefresh((r) => !r));
  };
  const uncollaborate = (collaborates) => {
    Promise.all([uncollaborateProduct(collaborates), uncollaborateProject(collaborates)])
      .catch((error) => console.error("Error fetching data:", error))
      .finally(() => setRefresh((r) => !r));
  };
  const deleteCollaborates = (collaborates) => {
    Promise.all([deleteProduct(collaborates), deleteProject(collaborates)])
      .catch((error) => console.error("Error fetching data:", error))
      .finally(() => {
        setSelectedProducts([]);
        setRefresh((r) => !r);
      });
  };
  const getProcesses = () => {
    Process.get_by_product_ids(selectedProducts.map((i) => i.id))
      .then((res) => res.json())
      .then((data) => setProcessData(data));
  };
  const getInvitationProducts = () => {
    InvitationCollaborateProduct.get_my_pending_invitations_received()
      .then((res) => res.json())
      .then((data) => setInvitationProducts(data));
  };
  const getInvitationProjects = () => {
    InvitationCollaborateProject.get_my_pending_invitations_received()
      .then((res) => res.json())
      .then((data) => setInvitationProjects(data));
  };

  const onSelectionChanged = (event) => {
    const selected = event.api.getSelectedRows();
    setSelectedProducts(selected);
  };
  const handleContextMenu = (e) => {
    e.preventDefault();
    setMenuPosition({ x: e.pageX, y: e.pageY });
    setMenuVisible(true);
  };
  const handleCloseMenu = () => {
    setMenuVisible(false);
  };
  const read = (ids) => Notification.read(ids).finally(() => setRefresh((r) => !r));

  const openDialogDelete = () => {
    if (dialogDeleteRef) {
      dialogDeleteRef.current.open();
    }
  };
  const columnDefs = [
    { field: "id", hide: true },
    { headerName: t("Name"), field: "name", cellStyle: { fontWeight: 600, ...leftAlignColDef.cellStyle }, ...defaultColDef },
    { headerName: t("Type"), field: "_type", ...defaultColDef, ...centerAlignColDef },
    { headerName: t("GWP (kgCO2)"), field: "gwp", cellStyle: { fontWeight: 500, ...rightAlignColDef.cellStyle }, ...defaultColDef, ...floatFormater },
  ];
  const retrieve = { label: "Retrieve", action: uncollaborate, needSelectedItems: true };
  const send = { label: "Send", action: handleOpenDialog, needSelectedItems: true };
  const delete_collaborate = { label: "Delete", action: openDialogDelete, needSelectedItems: true };
  let contextMenuOptionsMine = [retrieve];
  if (isCompanAdmin) {
    contextMenuOptionsMine.push(send);
  }
  contextMenuOptionsMine.push(delete_collaborate);

  let contextMenuOptionsInternal = [retrieve];
  if (isCompanAdmin) {
    contextMenuOptionsInternal.push(send);
    contextMenuOptionsInternal.push(delete_collaborate);
  }
  const contextMenuOptionsExternal = [
    { ...retrieve, label: "Retrieve to workspace" },
    { label: "Retrieve to internal", action: duplicateToMe, needSelectedItems: true },
    send,
    delete_collaborate,
  ];
  const is_mine = selectedTab === tabs[0].value;
  const is_internal = selectedTab === tabs[1]?.value;
  const is_external = selectedTab === tabs[2]?.value;

  const contextMenuOptions = is_mine ? contextMenuOptionsMine : is_internal ? contextMenuOptionsInternal : is_external ? contextMenuOptionsExternal : [];
  const productsGridProps = {
    rowSelection: {
      mode: "multiRow",
      headerCheckbox: true,
      checkboxes: true,
      enableClickSelection: true,
    },
    columnDefs: columnDefs,
    rowData: is_mine ? mineCollaborates : is_internal ? internalCollaborates : is_external ? externalCollaborates : [],
    onSelectionChanged: onSelectionChanged,
    getRowId: (params) => String(params.data.id),
  };
  useEffect(() => console.log(selectedProducts), [selectedProducts]);
  useEffect(() => console.log(user), [user]);
  useEffect(() => getExternalCollaborates(setExternalCollaborates), [refresh]);
  useEffect(() => getInternalCollaborates(setInternalCollaborates), [refresh]);
  useEffect(() => getMineCollaborates(setMineCollaborates), [refresh]);
  useEffect(() => {
    if (selectedProducts.length === 0) {
      setProcessData([]);
    } else {
      getProcesses();
    }
  }, [selectedProducts]);
  useEffect(() => getInvitationProducts(), [refresh]);
  useEffect(() => getInvitationProjects(), [refresh]);
  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.setGridOption("quickFilterText", searchText);
    }
  }, [searchText]);
  return (
    <MainContainer>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {invitationProducts.map((inv, i) => {
          return (
            <Box key={i} onClick={() => read([inv.id])}>
              <Divider sx={{ display: i === 0 ? "none" : "block" }} />
              <InvitationProduct invitation={inv} setRefresh={setRefresh} />
            </Box>
          );
        })}
        {invitationProjects.map((inv, i) => {
          return (
            <Box key={i} onClick={() => read([inv.id])}>
              <Divider sx={{ display: i === 0 ? "none" : "block" }} />
              <InvitationProject invitation={inv} setRefresh={setRefresh} />
            </Box>
          );
        })}
      </Box>
      <Box sx={{ paddingBottom: 2 }}>
        <ThirdTabs options={tabs} selected={selectedTab} setSelected={setSelectedTab} />
      </Box>
      <Paper variant="Table Container">
        <TableHeaderContainer>
          <TableTitleContainer>
            <Typography variant="Table Title">
              {is_mine && t(mine.label)}
              {is_internal && t(internal.label)}
              {is_external && t(external.label)} {t("Collaborate Library")}
            </Typography>
          </TableTitleContainer>
          <TableHeaderToolsContainer>
            <SearchBox text={searchText} setText={setSearchText} />
            <ExportAsXLSXButton data={externalCollaborates} label="Export as .xlsx" filename="products.xlsx" />
            <RefreshButton />
          </TableHeaderToolsContainer>
        </TableHeaderContainer>
        <Box>
          <ResizableBox
            width={Infinity}
            height={height}
            minConstraints={[100, 500]}
            maxConstraints={[Infinity, 800]}
            resizeHandles={["se"]}
            handle={(resizeHandle, ref) => (
              <Box
                ref={ref}
                className="custom-resize-handle"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "20px",
                  height: "20px",
                  cursor: "se-resize",
                }}>
                <Icon icon="vaadin:handle-corner" style={{ color: "grey", fontSize: "20px" }} />
              </Box>
            )}
            onResizeStop={(_, data) => {
              setHeight(data.size.height);
            }}>
            <Box className="ag-theme-alpine grid-container" onContextMenu={handleContextMenu}>
              <AgGridReact {...productsGridProps} ref={gridRef} domLayout="normal" />
            </Box>
          </ResizableBox>
        </Box>
      </Paper>
      <Box sx={{ display: selectedProducts.length === 1 ? "block" : "none", paddingY: 1 }}>
        <SystemBoundary processData={processData} />
        <Footprint processData={processData} pageType={pageType} />
        <SankeyDiagram processData={processData} />
      </Box>
      <DialogSend ref={dialogRef} selectedItems={selectedProducts} />
      <ContextMenu x={menuPosition.x} y={menuPosition.y} visible={menuVisible} onClose={handleCloseMenu} selectedItems={selectedProducts} options={contextMenuOptions} />
      <DialogDelete selectedItems={selectedProducts} handleDelete={deleteCollaborates} ref={dialogDeleteRef} />
    </MainContainer>
  );
};

export default CollaborateDashboard;
